// src/components/SmartLogoFrame.jsx
import React, { useEffect, useState } from "react";
import ImageWithFallback from "./ImageWithFallback";

const PUBLIC_NO_IMAGE = (process.env.PUBLIC_URL || "") + "/no-image.png";

function initials(name) {
  const clean = String(name || "Loop").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return (parts[0] || "LP").slice(0, 2).toUpperCase();
  return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase();
}

export function getBusinessLogo(biz) {
  if (!biz) return "";
  const candidates = [
    biz.logoUrl,
    biz.logo,
    biz.logo_url,
    biz.logoImage,
    biz.logo_image,
    biz.logoImg,
    biz.logoPath,
    biz.menuImage,
  ];

  for (const item of candidates) {
    if (!item) continue;
    if (typeof item === "string" && item.trim()) return item.trim();
    if (item && typeof item === "object" && item.url) return item.url;
  }

  return "";
}

export function normalizeLogoBusiness(raw = {}) {
  const logo = getBusinessLogo(raw);
  if (!logo) return raw;

  return {
    ...raw,
    logoUrl: logo,
    logo,
    menuImage: logo,
    imageUrl: logo,
    image: logo,
  };
}


function isLightNeutral(r, g, b) {
  const avg = (r + g + b) / 3;
  const spread = Math.max(r, g, b) - Math.min(r, g, b);
  return avg > 218 && spread < 42;
}

function similarToColor(r, g, b, a, color, tolerance = 36) {
  if (a < 18) return true;
  return (
    Math.abs(r - color.r) <= tolerance &&
    Math.abs(g - color.g) <= tolerance &&
    Math.abs(b - color.b) <= tolerance
  );
}

function averageCornerColor(data, w, h) {
  const sample = Math.max(4, Math.floor(Math.min(w, h) * 0.035));
  const zones = [
    [0, 0],
    [w - sample, 0],
    [0, h - sample],
    [w - sample, h - sample],
  ];
  let r = 0, g = 0, b = 0, a = 0, count = 0;
  zones.forEach(([sx, sy]) => {
    for (let y = sy; y < sy + sample; y += 1) {
      for (let x = sx; x < sx + sample; x += 1) {
        const i = (y * w + x) * 4;
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        a += data[i + 3];
        count += 1;
      }
    }
  });
  return { r: r / count, g: g / count, b: b / count, a: a / count };
}

async function buildCleanLogoPreview(src, bgColor, options = {}) {
  if (!src || typeof window === "undefined" || typeof document === "undefined") return "";

  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    const loaded = new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });
    img.src = src;
    await loaded;

    const sourceW = img.naturalWidth || img.width;
    const sourceH = img.naturalHeight || img.height;
    if (!sourceW || !sourceH) return "";

    const safePadRatio = Number.isFinite(Number(options.safePadRatio)) ? Math.max(0.006, Number(options.safePadRatio)) : 0.006;
    const drawScale = Number.isFinite(Number(options.drawScale)) ? Math.min(1, Math.max(0.72, Number(options.drawScale))) : 1;
    const outputMode = options.outputMode || "stage";
    const transparentOutput = options.background === "transparent";

    const sampleCanvas = document.createElement("canvas");
    const sampleMax = 900;
    const scale = Math.min(1, sampleMax / Math.max(sourceW, sourceH));
    const w = Math.max(1, Math.round(sourceW * scale));
    const h = Math.max(1, Math.round(sourceH * scale));
    sampleCanvas.width = w;
    sampleCanvas.height = h;
    const sampleCtx = sampleCanvas.getContext("2d", { willReadFrequently: true });
    sampleCtx.drawImage(img, 0, 0, w, h);

    const imageData = sampleCtx.getImageData(0, 0, w, h);
    const data = imageData.data;
    const edge = averageCornerColor(data, w, h);

    // Only remove baked gutters when the edge is a very light/neutral canvas.
    // This avoids destroying intentional brand backgrounds like pink/beige logos.
    if (!isLightNeutral(edge.r, edge.g, edge.b)) return "";

    let minX = w, minY = h, maxX = -1, maxY = -1;
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const i = (y * w + x) * 4;
        const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
        if (!similarToColor(r, g, b, a, edge, 34)) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX <= minX || maxY <= minY) return "";

    const pad = Math.round(Math.min(w, h) * safePadRatio);
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(w - 1, maxX + pad);
    maxY = Math.min(h - 1, maxY + pad);

    const cropW = maxX - minX + 1;
    const cropH = maxY - minY + 1;
    const cropArea = (cropW * cropH) / (w * h);

    // If almost the whole image is real content, leave it alone.
    if (cropArea > 0.88) return "";

    const sx = minX / scale;
    const sy = minY / scale;
    const sw = cropW / scale;
    const sh = cropH / scale;

    const out = document.createElement("canvas");

    if (outputMode === "crop") {
      // Export only the cropped logo area instead of a full 16:9 stage.
      // This removes the white rectangle around logos in result cards.
      const maxOut = 1440;
      const cropPad = Math.max(10, Math.round(Math.min(sw, sh) * Math.max(safePadRatio, 0.035)));
      const paddedW = sw + cropPad * 2;
      const paddedH = sh + cropPad * 2;
      const outScale = Math.min(1, maxOut / Math.max(paddedW, paddedH));
      const outW = Math.max(1, Math.round(paddedW * outScale));
      const outH = Math.max(1, Math.round(paddedH * outScale));
      out.width = outW;
      out.height = outH;
      const ctx = out.getContext("2d");
      if (!transparentOutput) {
        ctx.fillStyle = bgColor || "#f7efe6";
        ctx.fillRect(0, 0, outW, outH);
      }
      const dw = sw * outScale * drawScale;
      const dh = sh * outScale * drawScale;
      const dx = (outW - dw) / 2;
      const dy = (outH - dh) / 2;
      ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
      return out.toDataURL("image/png");
    }

    const outW = 1440;
    const outH = 810;
    out.width = outW;
    out.height = outH;
    const ctx = out.getContext("2d");
    if (!transparentOutput) {
      ctx.fillStyle = bgColor || "#f7efe6";
      ctx.fillRect(0, 0, outW, outH);
    }

    const fit = Math.min(outW / sw, outH / sh) * drawScale;
    const dw = sw * fit;
    const dh = sh * fit;
    const dx = (outW - dw) / 2;
    const dy = (outH - dh) / 2;
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);

    return out.toDataURL("image/png");
  } catch (_) {
    return "";
  }
}

export default function SmartLogoFrame({
  src,
  business,
  name = "",
  alt = "",
  className = "",
  variant = "card",
  rounded = 24,
  withBlur = false,
  fit = "contain",
  useCleanPreview = true,
  cleanPreviewSafePadRatio = 0.006,
  cleanPreviewDrawScale = 1,
  cleanPreviewOutputMode = "stage",
  cleanPreviewBackground = "brand",
  style = {},
}) {
  const [failed, setFailed] = useState(false);
  const logo = src || getBusinessLogo(business);
  const label = alt || name || business?.name || business?.businessName || "Logo";
  const brandBg =
    business?.logoBackgroundColor ||
    business?.brandBackgroundColor ||
    business?.mediaBackgroundColor ||
    "#f7efe6";
  const [cleanLogo, setCleanLogo] = useState("");
  const [cleanPreviewReady, setCleanPreviewReady] = useState(false);
  const [imageRatio, setImageRatio] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setImageRatio(null);
    if (!logo || failed || typeof window === "undefined") return undefined;

    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const w = img.naturalWidth || img.width || 0;
      const h = img.naturalHeight || img.height || 0;
      setImageRatio(w > 0 && h > 0 ? w / h : null);
    };
    img.onerror = () => {
      if (!cancelled) setImageRatio(null);
    };
    img.src = logo;

    return () => {
      cancelled = true;
    };
  }, [logo, failed]);

  const businessText = [
    business?.category,
    business?.businessType,
    business?.businessTypeLabel,
    business?.typeLabel,
    business?.mainType,
    business?.subtype,
    business?.__viewName,
    business?.name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const isEventLike =
    businessText.includes("evento") ||
    businessText.includes("event") ||
    businessText.includes("party") ||
    businessText.includes("pty");

  const resolvedFit =
    fit === "auto"
      ? (isEventLike || (typeof imageRatio === "number" && imageRatio >= 1.25) ? "cover" : "contain")
      : fit === "cover"
        ? "cover"
        : "contain";

  const mergedStyle = {
    "--smart-logo-bg": brandBg,
    "--smart-logo-fit": resolvedFit,
    ...style,
  };

  useEffect(() => {
    let cancelled = false;
    setCleanLogo("");
    setCleanPreviewReady(false);

    if (!logo || failed || !useCleanPreview || resolvedFit === "cover") {
      setCleanPreviewReady(true);
      return undefined;
    }

    buildCleanLogoPreview(logo, brandBg, {
      safePadRatio: cleanPreviewSafePadRatio,
      drawScale: cleanPreviewDrawScale,
      outputMode: cleanPreviewOutputMode,
      background: cleanPreviewBackground,
    }).then((next) => {
      if (!cancelled) {
        setCleanLogo(next || "");
        setCleanPreviewReady(true);
      }
    }).catch(() => {
      if (!cancelled) setCleanPreviewReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [logo, brandBg, failed, useCleanPreview, resolvedFit, cleanPreviewSafePadRatio, cleanPreviewDrawScale, cleanPreviewOutputMode, cleanPreviewBackground]);

  const isCleanPreviewPending = Boolean(logo && !failed && useCleanPreview && resolvedFit !== "cover" && !cleanPreviewReady);
  const displayLogo = resolvedFit === "cover" ? logo : (cleanLogo || logo);

  if (!logo || failed) {
    return (
      <div className={`smart-logo-frame smart-logo-${variant} smart-logo-fit-${resolvedFit} ${isCleanPreviewPending ? "smart-logo-clean-pending" : ""} ${className}`} style={{ borderRadius: rounded, ...mergedStyle }}>
        <style>{styles}</style>
        <div className="smart-logo-fallback"><span>{initials(label)}</span></div>
      </div>
    );
  }

  return (
    <div className={`smart-logo-frame smart-logo-${variant} smart-logo-fit-${resolvedFit} ${className}`} style={{ borderRadius: rounded, ...mergedStyle }}>
      <style>{styles}</style>

      {withBlur ? (
        <ImageWithFallback
          src={logo}
          alt=""
          className="smart-logo-blur"
          fallback={PUBLIC_NO_IMAGE}
          aria-hidden="true"
          style={{ objectFit: "cover" }}
        />
      ) : null}

      <div className="smart-logo-wash" />

      <div className="smart-logo-inner" style={{ "--smart-logo-bg": brandBg }}>
        <ImageWithFallback
          src={displayLogo}
          alt={label}
          className="smart-logo-img"
          fallback={PUBLIC_NO_IMAGE}
          onError={() => setFailed(true)}
          style={{ objectFit: resolvedFit }}
        />
      </div>
    </div>
  );
}

const styles = `
  .smart-logo-frame {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    isolation: isolate;
    background: transparent;
    border: 0;
    box-shadow: none;
  }

  .smart-logo-blur {
    position: absolute !important;
    inset: -18px !important;
    width: calc(100% + 36px) !important;
    height: calc(100% + 36px) !important;
    object-fit: cover !important;
    object-position: center !important;
    filter: blur(24px) saturate(1.15);
    transform: scale(1.24);
    opacity: .22;
    z-index: 0;
  }

  .smart-logo-wash {
    position: absolute;
    inset: 0;
    z-index: 1;
    background: transparent;
    pointer-events: none;
  }

  .smart-logo-inner {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 100%;
    padding: 0;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--smart-logo-bg, #f7efe6);
  }

  .smart-logo-hero .smart-logo-inner { padding: 0; }
  .smart-logo-list .smart-logo-inner { padding: 12px 14px; }
  .smart-logo-mini .smart-logo-inner { padding: 8px; }


  .smart-logo-fit-cover .smart-logo-inner {
    padding: 0;
  }

  .smart-logo-fit-cover .smart-logo-img {
    object-fit: cover !important;
  }

  .smart-logo-fit-contain .smart-logo-img {
    object-fit: contain !important;
  }

  .smart-logo-img {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: contain !important;
    object-position: center center !important;
    display: block !important;
    margin: auto !important;
    filter: none !important;
    transform: none !important;
  }

  .smart-logo-fallback {
    width: 100%;
    height: 100%;
    display: grid;
    place-items: center;
    background: var(--smart-logo-bg, #f7efe6);
  }

  .smart-logo-fallback span {
    min-width: 58px;
    height: 58px;
    padding: 0 18px;
    border-radius: 20px;
    background: #fff;
    color: #00675f;
    display: inline-grid;
    place-items: center;
    font-size: 22px;
    font-weight: 950;
    box-shadow: 0 16px 40px rgba(15,23,42,.10);
    border: 1px solid rgba(226,232,240,.9);
  }


  /* LOOP SMART LOGO — first paint no-flash mode
     When clean preview is enabled, do not briefly show the raw stored image
     with an old baked background. Show the correct frame background first,
     then reveal the cleaned preview when ready. */
  .smart-logo-clean-pending .smart-logo-img {
    opacity: 0 !important;
  }

  .smart-logo-clean-pending .smart-logo-inner {
    background: var(--smart-logo-bg, #f7efe6) !important;
  }

`;
